import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faPaperPlane,
    faSmile,
    faImage
} from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../Services/ApiServices';
import BlogCommentItem from './BlogCommentItem';
import extractErrorMessage from '../../Helpers/APIHelper';
import { storageHelper } from '../../Helpers/storageHelper';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import MiniLoader from '../Shared/MiniLoader';

const userId = storageHelper.getItem('user_id');

const BlogComment = ({ blogId, postOwnerId }) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchComments = async () => {
        try {
            setLoading(true);
            const response = await ApiServices.get(`Blogs/Comments/${blogId}`);
            setComments(response.data || []);
        } catch (error) {

        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentUser = async () => {
        try {
            const response = await ApiServices.get(`User/Profile/${userId}`);
            if (response.check) {
                setCurrentUser(response.data);
            }
        } catch (error) {

        }
    };

    useEffect(() => {
        fetchComments();
        fetchCurrentUser();
    }, [blogId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) {
            return;
        }

        if (!userId) {
            return;
        }

        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const commentData = {
                content: newComment,
                parent_id: null, // This is for top-level comments
                blog_id: parseInt(blogId),
                user_id: parseInt(userId)
            };

            const response = await ApiServices.post(`Blogs/Comments/Create`, commentData);
            if (response.check) {
                await fetchComments();
                setNewComment('');
            }
        } catch (error) {
            const title = error?.title || error?.message || "";
            if (title.toLowerCase().includes("validation errors")) {
                const errMessage = extractErrorMessage(error);
            } else {
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLike = async (commentId) => {
        try {
            const response = await ApiServices.put(`Blogs/Comments/Like/${commentId}`);
            if (response.check) {
                // Optimistic update or refetch
                await fetchComments();
            }
        } catch (error) {
        }
    };

    const handleDelete = async (commentId) => {
        try {
            const response = await ApiServices.delete(`Blogs/Comments/Delete/${commentId}`);
            if (response.check) {
                await fetchComments();
            }
        } catch (error) {
        }
    };

    const handleEdit = async (commentId, newContent) => {
        try {
            const response = await ApiServices.put(`Blogs/Comments/Update/${commentId}`, {
                content: newContent
            });

            if (response.check) {
                await fetchComments();
            }
        } catch (error) {
        }
    };

    // Callback when a reply is added in a child component
    const handleReplyAdded = () => {
        fetchComments();
    };

    const topLevelComments = comments.filter(comment => comment.parent_id === null);

    return (
        <div className="linkedin-comments-section">

            {/* Input Area */}
            <div className="comment-input-area" onClick={() => !userId}>
                <div className="user-avatar-small">
                    {currentUser?.image ? (
                        <img
                            src={getFullFileUrl(currentUser.image)}
                            alt={currentUser?.name || 'User'}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : (
                        <div className="default-avatar">
                            <FontAwesomeIcon icon={faUser} />
                        </div>
                    )}
                    <div className="default-avatar" style={{ display: (currentUser?.image && userId) ? 'none' : 'flex' }}>
                        <FontAwesomeIcon icon={faUser} />
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="input-form">
                    <div className="input-wrapper">
                        <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="أضف تعليقاً..."
                            disabled={isSubmitting}
                            className="linkedin-input"
                            readOnly={!userId}
                        />
                    </div>
                    {newComment.trim() && (
                        <button
                            type="submit"
                            className="post-btn"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'جاري النشر...' : 'نشر'}
                        </button>
                    )}
                </form>
            </div>

            {/* Comments List */}
            <div className="comments-list-container">
                {loading ? (
                    <div className="loader-wrapper">
                        <MiniLoader size={24} variant="dots" />
                    </div>
                ) : (
                    <>
                        {/* {topLevelComments.length > 0 && (
                             <h4 className="previous-comments-title">التعليقات القديمة</h4>
                        )} */}

                        {topLevelComments.map(comment => (
                            <BlogCommentItem
                                key={comment.id}
                                comment={comment}
                                onLike={handleLike}
                                onDelete={handleDelete}
                                onEdit={handleEdit}
                                onReplyAdded={handleReplyAdded}
                                currentUser={currentUser}
                                blogId={blogId}
                                userId={userId}
                                postOwnerId={postOwnerId}
                            />
                        ))}

                        {topLevelComments.length === 0 && (
                            <div className="no-comments-text">
                                كن أول من يعلق على هذا المنشور
                            </div>
                        )}
                    </>
                )}
            </div>

            <style jsx>{`
                .linkedin-comments-section {
                    background: #f9fafb; /* Light background for comments section */
                    padding: 16px;
                    direction: rtl;
                    border-radius: 8px;
                }

                .comment-input-area {
                    display: flex;
                    gap: 8px;
                    margin-bottom: 20px;
                    align-items: flex-start;
                }

                .user-avatar-small {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    overflow: hidden;
                    flex-shrink: 0;
                }
                
                .user-avatar-small img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .default-avatar {
                    width: 100%;
                    height: 100%;
                    background: #ccc;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }

                .input-form {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .input-wrapper {
                    position: relative;
                    border: 1px solid #c7c7c7;
                    border-radius: 24px;
                    background: white;
                    padding: 8px 16px;
                    display: flex;
                    align-items: center;
                    transition: border-color 0.2s;
                }

                .input-wrapper:focus-within {
                    border-color: #000;
                    background-color: #f0f2f5; 
                }

                .linkedin-input {
                    flex: 1;
                    border: none;
                    outline: none;
                    background: transparent;
                    font-size: 14px;
                }

                .post-btn {
                    align-self: flex-start;
                    background: #0a66c2;
                    color: white;
                    border: none;
                    border-radius: 16px;
                    padding: 4px 12px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                }

                .post-btn:disabled {
                    background: #ccc;
                    cursor: not-allowed;
                }

                .loader-wrapper {
                    display: flex;
                    justify-content: center;
                    padding: 20px;
                }

                .comments-list-container {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .previous-comments-title {
                    font-size: 0.9rem;
                    color: #666;
                    margin-bottom: 8px;
                }

                .no-comments-text {
                    text-align: center;
                    color: #666;
                    font-size: 0.9rem;
                    padding: 20px;
                }

                .auth-prompt-area {
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 16px;
                    text-align: center;
                    margin-bottom: 20px;
                }

                .auth-prompt-area p {
                    margin: 0;
                    color: #666;
                    font-size: 14px;
                }

                .auth-prompt-area a {
                    color: #0a66c2;
                    text-decoration: none;
                    font-weight: 600;
                }

                .auth-prompt-area a:hover {
                    text-decoration: underline;
                }

                @media (max-width: 576px) {
                    .linkedin-comments-section {
                        padding: 12px;
                    }
                    .user-avatar-small {
                        width: 32px;
                        height: 32px;
                    }
                }
            `}</style>
        </div>
    );
};

export default BlogComment;